// useOnlinePresence — register as a project function so pages can call it
// via el.call('useOnlinePresence') from onCreate. Returns a cleanup
// function to call from onRemove.
//
// Every caller subscribes to a single shared channel (`presence:symbols`)
// so the CalOnlineUsersBar surface reflects presence across the entire
// editor and governance suite — calendar, tickets, documents, canvas,
// workspaces. The optional `app` tag is tracked as presence metadata for
// per-app breakdowns later; it no longer scopes the channel itself.
//
// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.presence`
// dispatches through the SDK's transport-agnostic API. The transport is the
// SDK /user-socket presence registry (`sdk.subscribePresence`), selected by
// `sdkRealtimeProvider._openPresenceSocket` and installed in bootShell. The
// Supabase `postgres_changes` provider that used to serve this op was removed
// with the governance project — swapping transports again is a provider-only
// change; this file doesn't move.

import { getSDK } from './sdk.js'

// FA204 / frank trap — `_refCount`/`_unsub`/`_cancelled`/`_booted` used to
// live at module scope; `syncPresenceById`/`_bootChannel` were bare
// module-local helpers referenced from this file's one export. All are now
// self-contained inside `useOnlinePresence` — the state re-homed onto
// `window.__presenceState` (this file's flavor of the pointerCapability.js
// template) since it's genuine cross-call state (ref-counted across every
// independent `el.call('useOnlinePresence')` caller), and the two helpers
// duplicated straight in since they're this file's only caller.
export function useOnlinePresence({ app = 'symbols' } = {}) {
  const el = this
  const root = el.state.root
  const w = typeof window !== 'undefined' ? window : globalThis
  // Ref-counted shared subscription per browser tab. The workspace shell
  // subscribes once on mount so presence is available on every route
  // (CalOnlineUsersBar in AppHeader renders globally). Page-level
  // subscribes (canvas/app.js, calendar.js, tickets/board.js, etc.) add
  // themselves to the ref count without re-subscribing — and their
  // unmount cleanups only tear down the channel when the last subscriber
  // goes away. Previously cleanup was a singleton shared across callers,
  // so the first unmount nuked everyone else's presence stream.
  const state = w.__presenceState || {
    refCount: 0,
    unsub: null,
    cancelled: false,
    // Set synchronously the moment a boot commits to subscribing (the
    // unsub itself lands async through the SDK init-gate promise) so retry
    // ticks and later subscribers can't double-subscribe the channel.
    booted: false
  }
  w.__presenceState = state

  // Reconcile `root.presenceById` — a per-id reactive map (lowercased
  // email → 'online' | 'idle') maintained ALONGSIDE the canonical
  // `onlineUsers` array.
  //
  // Why a second structure: the team-and-agents board renders ~508
  // person/agent nodes, each with an online/idle dot. When presence lived
  // only on the flat `onlineUsers` array, every dot read (scanned) that
  // shared array — so ANY user's join/leave/idle change replaced the array
  // and re-ran EVERY dot's `hide` factory (a full-board tick that janks on
  // iPad). Keying presence per id lets each dot subscribe to
  // `presenceById[email]` — its OWN signal — so a status change fires only
  // the one node whose value actually changed.
  //
  // Each write goes through the nested signal store's per-key signal; the
  // signal's `===` dedupe no-ops unchanged statuses, so a sync where one
  // peer flips idle touches exactly one dot. Departed peers are set to
  // `undefined` (→ offline) rather than replacing the whole map (which
  // would re-fire every dot). The canonical `onlineUsers` array is written
  // separately for every off-board reader (AppHeader OnlineMembers,
  // PersonCard, chat mentions, analytics) — its shape is unchanged.
  const syncPresenceById = (r, nextById) => {
    // First sync lazily creates the nested store. Reading
    // `root.presenceById` outside a reactive effect is side-effect-free
    // (no observer to subscribe), so this guard is free.
    if (!r.presenceById) r.update({ presenceById: {} })
    const byId = r.presenceById
    if (!byId) return
    // Present peers — unchanged statuses are no-op'd by the signal's ===
    // guard.
    for (const email in nextById) byId[email] = nextById[email]
    // Departed peers — clear to offline without replacing the map.
    // Iterate a snapshot of the current keys so writing back is safe.
    for (const email of Object.keys(byId)) {
      if (!(email in nextById)) byId[email] = undefined
    }
  }

  // Boot (or retry-boot) the shared channel. Subscribes through the SDK's
  // flat `subscribePresence` (the /user-socket transport) DIRECTLY — the
  // old wp.realtime provider indirection added nothing except an ordering
  // dependency: when the shell's first `useOnlinePresence` ran before
  // bootShell installed the realtime provider (or before auth populated
  // currentUserEmail), the one-shot boot silently no-op'd and presence
  // stayed DEAD for the whole session — the original "online members never
  // show" flow bug. Now the boot RETRIES (bounded, 1s steps) until the SDK
  // + auth email are ready, so whichever order boot lands in, the channel
  // comes up.
  const bootChannel = (r, a, attempt = 0) => {
    if (state.cancelled || state.booted) return
    const me = r?.currentUserEmail
    const sdk = getSDK()
    if (!me || typeof sdk?.subscribePresence !== 'function') {
      if (attempt < 60) setTimeout(() => bootChannel(r, a, attempt + 1), 1000)
      else console.warn('[useOnlinePresence] gave up — sdk/auth never became ready')
      return
    }
    state.booted = true
    // Signature of the last onlineUsers set we actually wrote — lets the
    // sync handler skip redundant writes when a re-sync carries an
    // unchanged set (see the change-guard below).
    let lastOnlineSig = null
    // The shell's SDK bootstrap wraps every method in an init gate
    // returning a PROMISE of the real return — resolve it so unsub is the
    // actual unsubscribe function (a stored Promise made session teardown
    // leak the socket: the `typeof === 'function'` cleanup guard skipped
    // it).
    const pending = sdk.subscribePresence(
      { scope: 'symbols', userKey: me, meta: { app: a } },
      (s) => {
        // `s` is the presenceState-shaped roster { [key]: [{ online_at,
        // ...meta }] }. Re-derive the full online list on each sync so
        // root.onlineUsers stays the canonical view.
        const seen = new Set()
        const list = []
        // Per-id presence map built in the same pass — see syncPresenceById.
        const nextById = {}
        for (const key of Object.keys(s || {})) {
          if (seen.has(key)) continue
          seen.add(key)
          const meta = s[key]?.[0] || {}
          // Activity status from the peer's tracked meta. Defaults to
          // 'online' so any peer missing the field still counts as online —
          // the binary "present === online" semantics are preserved.
          const status = meta.status || 'online'
          list.push({
            email: key,
            since: meta.online_at || Date.now(),
            app: meta.app || null,
            status
          })
          nextById[String(key).toLowerCase()] = status
        }
        // Canonical array for every off-board reader (shape unchanged).
        // Change-guard: re-syncs often carry an UNCHANGED online set.
        // Writing unconditionally re-runs EVERY onlineUsers subscriber (the
        // presence widget + every /people card) for nothing, on a global
        // signal. Only write when the emails/statuses actually changed.
        const sig = list
          .map((u) => `${u.email}:${u.status}`)
          .sort()
          .join('|')
        if (sig !== lastOnlineSig) {
          lastOnlineSig = sig
          r.update({ onlineUsers: list })
        }
        // Per-id fan-out so the team-and-agents board repaints only the
        // dots whose status changed, not all ~508 on every tick.
        syncPresenceById(r, nextById)
      }
    )
    Promise.resolve(pending)
      .then((u) => {
        if (state.cancelled) {
          try {
            if (typeof u === 'function') u()
          } catch (_) {}
          state.booted = false
          return
        }
        state.unsub = typeof u === 'function' ? u : null
      })
      .catch((err) => {
        state.booted = false
        console.warn('[useOnlinePresence] subscribe failed:', err?.message || err)
      })
  }

  state.refCount++

  // First subscriber boots the channel (with readiness retries — see
  // bootChannel). Later subscribers just ride the ref count.
  if (state.refCount === 1) {
    state.cancelled = false
    try {
      bootChannel(root, app)
    } catch (err) {
      console.warn('[useOnlinePresence] subscribe failed:', err?.message || err)
    }
  }

  // Per-caller cleanup: decrements the ref count; tears down the channel
  // (and clears `root.onlineUsers`) when the last subscriber leaves.
  let called = false
  return () => {
    if (called) return
    called = true
    state.refCount = Math.max(0, state.refCount - 1)
    if (state.refCount === 0) {
      state.cancelled = true
      state.booted = false
      if (typeof state.unsub === 'function') {
        try {
          state.unsub()
        } catch (_) {}
      }
      state.unsub = null
    }
  }
}
